import type { PorterCallbackData } from "../domain/types/porter-callback.types";
import { PORTER_CALLBACK_QUERY_PARAMS } from "../domain/constants/porter-callback.constants";
import { KNOWN_BASE_DOMAINS } from "@shared/utils/subdomain-url.utils";

const PORTER_CALLBACK_COOKIE_NAME = "planta_porter_cb";

function deriveCookieDomain(): string | null {
  const hostname = window.location.hostname;
  for (const baseDomain of KNOWN_BASE_DOMAINS) {
    if (hostname === baseDomain || hostname.endsWith(`.${baseDomain}`)) {
      return `.${baseDomain}`;
    }
  }
  return null;
}

const COOKIE_KEYS = {
  PID: "p_cb_pid",
  VERSION: "p_cb_v",
} as const;

let hasCallbackBeenSent = false;

/**
 * Minimum time (ms) before callback can be sent after page load.
 * Prevents false positives from visibility changes during page navigation/load.
 */
const MIN_TIME_BEFORE_CALLBACK_MS = 1500;

let callbackCapturedAt: number | null = null;

// eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- User-provided data that will be JSON.stringify'd, this IS a system boundary
let callbackDataPayload: Record<string, unknown> | null = null;

interface CookieDataShape {
  // eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- Cookie data is external storage, this IS a system boundary
  readonly [key: string]: unknown;
}

function isCookieDataShape(value: unknown): value is CookieDataShape {
  return typeof value === "object" && value !== null;
}

function parseCookieData(jsonString: string): PorterCallbackData | null {
  // eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- Cookie data is external storage, this IS a system boundary
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonString);
  } catch {
    return null;
  }

  if (!isCookieDataShape(parsed)) {
    return null;
  }

  const pid = parsed[COOKIE_KEYS.PID];
  const version = parsed[COOKIE_KEYS.VERSION];

  if (typeof pid !== "string" || typeof version !== "string") {
    return null;
  }

  return {
    callbackIdPublic: pid,
    callbackVersion: version,
  };
}

export function getPorterCallbackCookie(): PorterCallbackData | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === PORTER_CALLBACK_COOKIE_NAME && value !== undefined) {
      const decoded = decodeURIComponent(value);
      return parseCookieData(decoded);
    }
  }
  return null;
}

export function setPorterCallbackCookie(data: PorterCallbackData): void {
  const cookieData = {
    [COOKIE_KEYS.PID]: data.callbackIdPublic,
    [COOKIE_KEYS.VERSION]: data.callbackVersion,
  };
  const encoded = encodeURIComponent(JSON.stringify(cookieData));
  const domain = deriveCookieDomain();
  const domainAttr = domain !== null ? `; domain=${domain}` : "";
  document.cookie = `${PORTER_CALLBACK_COOKIE_NAME}=${encoded}; path=/${domainAttr}; SameSite=Lax`;
}

/**
 * Delete porter callback cookie. Must reuse the same domain attribute used when
 * setting it, otherwise the browser won't match and the cookie won't be removed.
 */
export function deletePorterCallbackCookie(): void {
  const domain = deriveCookieDomain();
  const domainAttr = domain !== null ? `; domain=${domain}` : "";
  document.cookie = `${PORTER_CALLBACK_COOKIE_NAME}=; path=/${domainAttr}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
}

export function extractPorterCallbackParams(
  searchParams: URLSearchParams,
): PorterCallbackData | null {
  const pid = searchParams.get(PORTER_CALLBACK_QUERY_PARAMS.PID);
  const version = searchParams.get(PORTER_CALLBACK_QUERY_PARAMS.VERSION);

  if (
    pid !== null &&
    version !== null &&
    pid.length > 0 &&
    version.length > 0
  ) {
    return { callbackIdPublic: pid, callbackVersion: version };
  }
  return null;
}

function getPorterCallbackEndpoint(): string {
  // eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- Environment variables are external runtime input
  const envUrl: unknown = import.meta.env["VITE_API_BASE_URL"];
  const baseUrl = typeof envUrl === "string" ? envUrl : "";
  return `${baseUrl}/api/v1/porter?cb=true`;
}

function sendPorterCallback(
  callbackData: PorterCallbackData,
  durationMs: number | null,
): void {
  const endpoint = getPorterCallbackEndpoint();

  // eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- HTTP POST body is a system boundary
  const bodyData: Record<string, unknown> = {
    [COOKIE_KEYS.PID]: callbackData.callbackIdPublic,
    [COOKIE_KEYS.VERSION]: callbackData.callbackVersion,
  };

  if (durationMs !== null) {
    bodyData["d"] = durationMs;
  }

  if (callbackDataPayload !== null) {
    bodyData["data"] = JSON.stringify(callbackDataPayload);
  }

  const body = JSON.stringify(bodyData);

  if (typeof navigator.sendBeacon === "function") {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(endpoint, blob);
  } else {
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      // Ignore errors during page close
    });
  }
}

/**
 * Sends the callback once and clears the cookie. Suppressed within
 * MIN_TIME_BEFORE_CALLBACK_MS of page load to avoid false positives from visibility changes during navigation/app-switching.
 */
function handlePageClose(): void {
  if (hasCallbackBeenSent) {
    return;
  }

  const callbackData = getPorterCallbackCookie();
  if (callbackData === null) {
    return;
  }

  const durationMs =
    callbackCapturedAt !== null ? Date.now() - callbackCapturedAt : null;

  // Safeguard: Don't fire callback if it's too soon after page load
  // This prevents false positives from visibility changes during navigation
  if (durationMs !== null && durationMs < MIN_TIME_BEFORE_CALLBACK_MS) {
    return;
  }

  // Mark as sent to prevent duplicate sends
  hasCallbackBeenSent = true;

  deletePorterCallbackCookie();

  sendPorterCallback(callbackData, durationMs);
}

function setupPageCloseDetection(): void {
  const handleBeforeUnload = (): void => {
    handlePageClose();
  };

  const handlePageHide = (): void => {
    handlePageClose();
  };

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      handlePageClose();
    }
  };

  const handleUnload = (): void => {
    handlePageClose();
  };

  window.addEventListener("beforeunload", handleBeforeUnload);
  window.addEventListener("pagehide", handlePageHide);
  window.addEventListener("unload", handleUnload);
  document.addEventListener("visibilitychange", handleVisibilityChange);
}

export function capturePorterCallbackFromUrl(): void {
  const url = new URL(window.location.href);
  const callbackData = extractPorterCallbackParams(url.searchParams);

  if (callbackData !== null) {
    setPorterCallbackCookie(callbackData);

    // Record capture time for the safeguard check
    callbackCapturedAt = Date.now();

    url.searchParams.delete(PORTER_CALLBACK_QUERY_PARAMS.PID);
    url.searchParams.delete(PORTER_CALLBACK_QUERY_PARAMS.VERSION);
    window.history.replaceState({}, "", url.toString());
  } else if (
    getPorterCallbackCookie() !== null &&
    callbackCapturedAt === null
  ) {
    callbackCapturedAt = Date.now();
  }

  setupPageCloseDetection();
}

export const PorterCallback = {
  hasActiveCallback(): boolean {
    return getPorterCallbackCookie() !== null;
  },

  // eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- User-provided data API
  getData(): Record<string, unknown> | null {
    return callbackDataPayload;
  },

  // eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- User-provided data API
  setData(data: Record<string, unknown>): void {
    callbackDataPayload = data;
  },

  clearData(): void {
    callbackDataPayload = null;
  },
} as const;
